import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        // Bigger, higher-contrast toasts. We opt out of sonner's default
        // background + border classes when the toast has a semantic type
        // (success / error / warning / info) so the accent colour fills
        // the whole row instead of just a small icon. Text size bumped
        // to `text-base` and padding bumped to `p-4` so toasts are easy
        // to read from across the room.
        classNames: {
          toast:
            "group toast group-[.toaster]:border group-[.toaster]:shadow-xl group-[.toaster]:rounded-xl group-[.toaster]:text-base group-[.toaster]:p-4 group-[.toaster]:min-h-[64px] group-[.toaster]:w-[420px]",
          title: "group-[.toast]:text-base group-[.toast]:font-semibold",
          description: "group-[.toast]:text-sm group-[.toast]:opacity-90",
          actionButton:
            "group-[.toast]:bg-white/20 group-[.toast]:text-white group-[.toast]:hover:bg-white/30",
          cancelButton:
            "group-[.toast]:bg-white/10 group-[.toast]:text-white group-[.toast]:hover:bg-white/20",
          // Semantic variants — solid fills, strong borders, white text.
          success:
            "group-[.toaster]:!bg-emerald-600 group-[.toaster]:!border-emerald-700 group-[.toaster]:!text-white",
          error:
            "group-[.toaster]:!bg-red-600 group-[.toaster]:!border-red-700 group-[.toaster]:!text-white",
          warning:
            "group-[.toaster]:!bg-amber-500 group-[.toaster]:!border-amber-600 group-[.toaster]:!text-white",
          info:
            "group-[.toaster]:!bg-sky-600 group-[.toaster]:!border-sky-700 group-[.toaster]:!text-white",
          // Close button tuned so it stays visible on the coloured bg.
          closeButton:
            "group-[.toast]:bg-white/20 group-[.toast]:border-white/40 group-[.toast]:text-white group-[.toast]:hover:bg-white/30",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
